export const M10_UNIT_5 = {
  id: "m10-u5-before-after",
  title: "Unit 5: Before & After",
  topic: "Past Perfect + Visual Arts & Life Events",
  iconType: "palette",
  grammarImage: "/m10-u5-before-after.jpg",
  grammarText: "Unit focus: Past Perfect (had + past participle) to describe events that happened before another past event — sequencing life stories, art careers, and turning points.",

  sets: [

    // ===================================================================
    // SET 1: LIFE EVENTS & SEQUENCE (B1+)
    // ===================================================================
    {
      id: "m10-u5-set1",
      title: "Set 1: Things That Had Already Happened",
      context: `*Tokyo, Monday morning — Yuki's story*

Yuki is 22 and has just arrived in London to study art.
She tells her new flatmate about her life back in Japan.
"By the time I left Tokyo, so much had already changed."

*Tokio, lunes por la mañana — La historia de Yuki*

Yuki tiene 22 años y acaba de llegar a Londres a estudiar arte.
Le cuenta a su nueva compañera de piso sobre su vida en Japón.
"Para cuando me fui de Tokio, ya habían cambiado muchas cosas."`,

      questions: [
        {
          type: "match",
          q: "Match the life events to their meaning:",
          pairs: [
            { left: "graduate", right: "graduarse" },
            { left: "fail an exam", right: "reprobar un examen" },
            { left: "quit a job", right: "renunciar a un trabajo" }
          ],
          a: "completed",
          successTip: "Quit = renunciar/dejar. Also used in slang: 'Quit it!' = ¡Para ya! Very common in casual English! 🛑"
        },

        {
          type: "mc",
          q: "Yuki: 'Before I came to London, I ___ already ___ from university.'",
          options: ["had / graduated", "have / graduated", "had / graduate"],
          a: "had / graduated",
          tip: "Past Perfect = had + past participle."
        },

        {
          type: "mc",
          q: "She ___ never ___ abroad before this trip.",
          options: ["had / lived", "have / lived", "had / live"],
          a: "had / lived",
          tip: "Had never + past participle = never before that moment."
        },

        {
          type: "input",
          q: "Yuki's story: 'I ___ ___ my job at the café before I bought my plane ticket.')",
          a: "had quit",
          alts: ["Had quit","had left", "had quit", "had lost", "had started", "had found", "had changed", "had finished", "had kept"],
          tip: "Quit → quit (irregular, same form). Had quit."
        },

        {
          type: "match",
          q: "Match more life events:",
          pairs: [
            { left: "move abroad", right: "mudarse al extranjero" },
            { left: "start over", right: "empezar de nuevo" },
            { left: "pursue a dream", right: "perseguir un sueño" }
          ],
          a: "completed",
          successTip: "Start over = empezar de cero. Phrasal verb! Also: 'start from scratch' = mismo significado. Both super common! 🔄"
        },

        {
          type: "mc",
          q: "By the time Yuki landed in London, she ___ already ___ her accommodation online.",
          options: ["had / booked", "has / booked", "had / book"],
          a: "had / booked",
          tip: "By the time + Past Simple → Past Perfect for earlier action.",
          successTip: "As a verb, book means to reserve or arrange something in advance; common synonyms are reserve and schedule"
        },

        {
          type: "mc",
          q: "Yuki: 'I ___ never ___ anyone from Egypt before I met Omar.'",
          options: ["had / met", "have / met", "had / meet"],
          a: "had / met",
          tip: "Had never met = nunca había conocido."
        },

        {
          type: "order",
          q: "Traduce: 'Ella ya había renunciado a su trabajo cuando decidió estudiar arte.'",
          words: ["She", "had", "already", "quit", "her", "job", "when", "she", "decided", "to", "study", "art."],
          a: "She had already quit her job when she decided to study art.",
          extra: ["has", "quitted", "decides"],
          successTip: "“Quitted” exists, but quit is the normal and more natural past form in modern English, so say “I quit my job,” not “I quitted my job."
        },

        {
          type: "mc",
          q: "Yuki admits: 'I ___ failed my university entrance exam twice before I passed.'",
          options: ["had", "have", "was"],
          a: "had",
          tip: "Earlier past event = had + past participle.",
          successTip: "Fail an exam = reprobar. Slang: 'bomb a test' = reprobar miserablemente. 'I totally bombed it.' Very casual! 💣"
        },

        {
          type: "mc",
          q: "Before she ___ her dream, Yuki ___ years working in an office.",
          options: ["pursued / had spent", "had pursued / spent", "pursues / had spent"],
          a: "pursued / had spent",
          tip: "First event (spent years) = Past Perfect. Later event (pursued) = Past Simple."
        }
      ]
    },

    // ===================================================================
    // SET 2: VISUAL ARTS VOCABULARY (B1+)
    // ===================================================================
    {
      id: "m10-u5-set2",
      title: "Set 2: The World of Art",
      context: `*Cairo, years earlier — Omar's studio*

Omar is 35 and has been an art teacher for ten years.
Before he became a teacher, he had worked as a ceramicist.
His hands still remember the feeling of clay.

*El Cairo, años antes — El estudio de Omar*

Omar tiene 35 años y lleva diez años siendo profesor de arte.
Antes de convertirse en maestro, había trabajado como ceramista.
Sus manos todavía recuerdan la sensación de la arcilla.`,

      questions: [
        {
          type: "match",
          q: "Match the art materials:",
          pairs: [
            { left: "clay", right: "arcilla" },
            { left: "ceramic", right: "cerámica" },
            { left: "pottery", right: "alfarería" }
          ],
          a: "completed",
          successTip: "Clay (arcilla) → shaped and fired → ceramic (cerámica). Pottery = the craft/objects. All connected! 🏺"
        },

        {
          type: "mc",
          q: "Omar had worked with ___ for years before he tried oil painting.",
          options: ["clay", "canvas", "glaze"],
          a: "clay",
          tip: "Clay = arcilla (used for pottery and ceramics).",
          successTip: "clay = arcilla, canvas = lienzo, glaze = esmalte. All essential art materials! 🎨"
        },

        {
          type: "input",
          q: "Gallery description: 'The exhibition featured beautiful hand-made ___.'",
          a: "pottery",
          alts: ["Pottery","sculptures", "paintings", "artworks", "ceramics", "pottery", "creations", "pieces", "crafts", "decorations"],
          tip: "Pottery = alfarería (objects made from clay)."
        },

        {
          type: "match",
          q: "Match more art vocabulary:",
          pairs: [
            { left: "canvas", right: "lienzo (para pintar)" },
            { left: "paintbrush", right: "pincel" },
            { left: "glaze", right: "esmalte/barniz (para cerámica)" }
          ],
          a: "completed",
          successTip: "Also: 'glazed over' = mirada perdida. 'His eyes glazed over during the lecture.' 😶"
        },

        {
          type: "mc",
          q: "Omar had stretched the ___ before he started painting.",
          options: ["canvas", "clay", "glaze"],
          a: "canvas",
          tip: "Canvas = lienzo (fabric surface for painting).",
          successTip: "'Stretch a canvas' = preparar el lienzo. Idiom: 'stretch the truth' = exagerar. Same verb, very different meanings! 🖼️"
        },

        {
          type: "mc",
          q: "The museum ___ a new exhibition of ancient Egyptian ceramics.",
          options: ["had opened", "opens", "open"],
          a: "had opened",
          tip: "Event before the story = Past Perfect."
        },

        {
          type: "match",
          q: "Match art world vocabulary:",
          pairs: [
            { left: "gallery", right: "galería de arte" },
            { left: "sculpture", right: "escultura" },
            { left: "exhibition", right: "exposición" }
          ],
          a: "completed"
        },

        {
          type: "input",
          q: "Omar's memory: 'By the time I was 25, I had already sold my first ___ at the local gallery.'",
          a: "sculpture",
          alts: ["Sculpture","painting", "artwork", "piece", "photograph", "portrait", "canvas", "ceramic piece", "drawing"],
          tip: "Sculpture = escultura (3D art work)."
        },

        {
          type: "mc",
          q: "Omar had studied at three different ___ before opening his own studio.",
          options: ["galleries", "glazes", "canvases"],
          a: "galleries",
          tip: "Gallery (pl. galleries) = where art is displayed.",
          successTip: "Gallery = galería. Informal: 'the whole nine yards' = todo lo relacionado. 'He'd done the whole nine yards — galleries, auctions, museums.' 🎨"
        },

        {
          type: "mc",
          q: "Before Omar became a teacher, he ___ his ceramics at street markets.",
          options: ["had sold", "sold", "has sold"],
          a: "had sold",
          tip: "Before + Past Simple → Past Perfect for earlier action."
        }
      ]
    },

    // ===================================================================
    // SET 3: HAD + PAST PARTICIPLE FORMATION (B1+)
    // ===================================================================
    {
      id: "m10-u5-set3",
      title: "Set 3: Shapes of the Past",
      context: `*London art school — Yuki's first week*

Yuki sits in her first class and listens carefully.
Her teacher asks: "What had you done with art before you came here?"
Yuki thinks of everything that had brought her to this room.

*Escuela de arte en Londres — Primera semana de Yuki*

Yuki se sienta en su primera clase y escucha con atención.
Su maestra pregunta: "¿Qué habías hecho con el arte antes de venir aquí?"
Yuki piensa en todo lo que la había traído a esa sala.`,

      questions: [
        {
          type: "match",
          q: "Match the irregular past participles:",
          pairs: [
            { left: "make", right: "made" },
            { left: "see", right: "seen" },
            { left: "build", right: "built" }
          ],
          a: "completed",
          successTip: "Irregular past participles are key! Make→made, see→seen, build→built. Learn them as chunks, not rules! 🧠"
        },

        {
          type: "mc",
          q: "Yuki: 'Before this class, I had never ___ a real sculpture in a gallery.'",
          options: ["seen", "saw", "see"],
          a: "seen",
          tip: "See → seen (irregular past participle)."
        },

        {
          type: "input",
          q: "Yuki recalls: 'I had ___ dozens of small clay figures before I arrived.' (make)",
          a: "made",
          alts: ["Made"],
          tip: "Make → made (irregular).",
          successTip: "“Recall” is a more formal way to say remember and often sounds more reflective; similar words are remember, think back, and look back on."
        },

        {
          type: "mc",
          q: "By the time she enrolled, Yuki had ___ her own pottery kiln at home.",
          options: ["built", "build", "builded"],
          a: "built",
          tip: "Build → built (irregular).",
          successTip: "Build → built → built. Also idiom: 'built from the ground up' = construido desde cero. 'She built her career from the ground up.' 💪"
        },

        {
          type: "match",
          q: "Match more irregular past participles:",
          pairs: [
            { left: "write", right: "written" },
            { left: "teach", right: "taught" },
            { left: "sell", right: "sold" }
          ],
          a: "completed"
        },

        {
          type: "mc",
          q: "Omar had ___ ceramics for twenty years before he wrote his first book.",
          options: ["taught", "teached", "teach"],
          a: "taught",
          tip: "Teach → taught (irregular)."
        },

        {
          type: "mc",
          q: "Lucia had ___ three sculptures before her first gallery show.",
          options: ["sold", "sell", "selled"],
          a: "sold",
          tip: "Sell → sold (irregular)."
        },

        {
          type: "order",
          q: "Traduce: 'Ella había escrito su propuesta antes de que comenzara el concurso.'",
          words: ["She", "had", "written", "her", "proposal", "before", "the", "competition", "started."],
          a: "She had written her proposal before the competition started.",
          extra: ["has", "proposition", "begun"],
          tip: "Begun is the past participle of begin, not the past simple",
          successTip: "“Proposition” usually means an idea, offer, or statement, not a formal written plan, so proposal is the natural word in this sentence."
        },

        {
          type: "input",
          q: "Yuki's diary: 'I had never ___ so far from home before this trip.' (go → past participle)",
          a: "been",
          alts: ["lived", "traveled", "gone", "stayed", "moved", "wandered"],
          tip: "Be → been (irregular past participle)."
        },

        {
          type: "mc",
          q: "Before the class ended, Yuki ___ already ___ her first piece in London.",
          options: ["had / finished", "has / finished", "had / finish"],
          a: "had / finished",
          tip: "Had + finished = Past Perfect."
        }
      ]
    },

    // ===================================================================
    // SET 4: ALREADY, NEVER, JUST & YET (B2-)
    // ===================================================================
    {
      id: "m10-u5-set4",
      title: "Set 4: First Times & Completed Moments",
      context: `*London gallery — Omar visits for the first time*

Omar arrives at a prestigious London gallery.
He had never been to London before this trip.
By the time he walked in, the exhibition had already started.
He had just missed the opening speech — but he didn't mind.

*Galería de Londres — Omar visita por primera vez*

Omar llega a una prestigiosa galería en Londres.
Nunca había estado en Londres antes de este viaje.
Para cuando entró, la exposición ya había comenzado.
Justo se había perdido el discurso de apertura, pero no le importó.`,

      questions: [
        {
          type: "match",
          q: "Match the adverbs to their meaning:",
          pairs: [
            { left: "already", right: "ya (antes de lo esperado)" },
            { left: "never", right: "nunca (hasta ese momento)" },
            { left: "just", right: "justo/recién (momento muy reciente)" }
          ],
          a: "completed",
          successTip: "Position matters! Already/never/just go BETWEEN had and the participle: 'had already seen', 'had never tried', 'had just arrived'! ✅"
        },

        {
          type: "mc",
          q: "Omar had ___ seen so many masterpieces in one room before.",
          options: ["never", "already", "just"],
          a: "never",
          tip: "Never = nunca (before that moment)."
        },

        {
          type: "mc",
          q: "By the time he found the ceramics section, the crowd had ___ moved on.",
          options: ["already", "never", "just"],
          a: "already",
          tip: "Already = ya (happened before expected)."
        },

        {
          type: "input",
          q: "Omar's diary: 'I had ___ arrived when someone recognised my work on the wall.'",
          a: "just",
          alts: ["barely", "only just", "hardly", "scarcely"],
          tip: "Had just arrived = acababa de llegar.",
          successTip: "'Had just' = acababa de. Very common in storytelling! 'She had just sat down when the phone rang.' Perfect combo! 📱"
        },

        {
          type: "mc",
          q: "Yuki had ___ visited three galleries that morning when she ran into Omar.",
          options: ["already", "never", "yet"],
          a: "already",
          tip: "Already = done more than expected by that point."
        },

        {
          type: "mc",
          q: "Omar admitted: 'I had ___ tried working with oil paint — clay was always my thing.'",
          options: ["never", "already", "just"],
          a: "never",
          tip: "Had never tried = nunca había intentado.",
          successTip: "'My thing' = lo mío / mi pasión. Slang! 'Clay was always his thing.' Very natural in informal English! 🎯"
        },

        {
          type: "order",
          q: "Traduce: 'Para cuando llegó Omar, Yuki ya había terminado su visita.'",
          words: ["By", "the", "time", "Omar", "arrived,", "Yuki", "had", "already", "finished", "her", "visit."],
          a: "By the time Omar arrived, Yuki had already finished her visit.",
        },

        {
          type: "mc",
          q: "The gallery had ___ sold three of Omar's pieces before he even arrived.💵💵",
          options: ["already", "never", "just"],
          a: "already",
          tip: "Already = before he expected/arrived."
        },

        {
          type: "mc",
          q: "Omar had ___ heard of this artist — she was completely new to him.",
          options: ["never", "already", "just"],
          a: "never",
          tip: "Never heard of = nunca había oído hablar de.",
          successTip: "'Never heard of' = nunca había oído hablar de. Phrasal: 'ring a bell' = sonar familiar. 'Does her name ring a bell?' 🔔"
        },

        {
          type: "input",
          q: "Gallery story: 'The curator had ___ stepped out when the fire alarm went off.'",
          a: "just",
          alts: ["barely", "only just", "hardly", "scarcely"],
          tip:"Already” is grammatical, but just is more natural when one action happened only moments before another. For clean, natural phrasing, the more precise choice usually sounds better.",
          successTip: "'Just stepped out' = acababa de salir. Office slang: 'He's just stepped out' = salió un momento. Used constantly! 🚪"
        }
      ]
    },

    // ===================================================================
    // SET 5: ART SCHOOL STORIES (B2-)
    // ===================================================================
    {
      id: "m10-u5-set5",
      title: "Set 5: The Road to Art School",
      context: `*São Paulo — Lucia's story*

Lucia is 28 and a sculptor based in London.
Her path to art was not straightforward.
"Before I got into art school, I had already dropped out of law school twice."
"People thought I had lost the plot — but I knew what I wanted."
"She had slowly built up a portfolio of thirty pieces over three years."

*São Paulo — La historia de Lucia*

Lucia tiene 28 años y es una escultora radicada en Londres.
Su camino hacia el arte no fue sencillo.
"Antes de entrar a la escuela de arte, ya había abandonado la escuela de derecho dos veces."
"La gente pensaba que me había vuelto loca, pero yo sabía lo que quería."
"Habiá construido un portafolio de treinta piezas en tres años."`,

      questions: [
        {
          type: "match",
          q: "Match the art school vocabulary:",
          pairs: [
            { left: "enroll", right: "inscribirse" },
            { left: "drop out", right: "abandonar (una carrera)" },
            { left: "scholarship", right: "beca" }
          ],
          a: "completed",
          successTip: "Drop out = abandonar. Famous dropouts: Steve Jobs, Mark Zuckerberg. 'College dropout' is even a Kanye album! 🎓"
        },

        {
          type: "mc",
          q: "Lucia had ___ from law school before she enrolled in art school.",
          options: ["dropped out", "dropped off", "dropped in"],
          a: "dropped out",
          tip: "Drop out = abandonar (estudios).",
          successTip: "Drop out vs Drop off vs Drop in! Drop OUT = leave school. Drop OFF = dejar a alguien. Drop IN = visitar de improviso. 🚗"
        },

        {
          type: "input",
          q: "Lucia's story: 'I had applied for a ___ three times to help me pay for my studies.'",
          a: "scholarship",
          alts: ["Scholarship"],
          tip: "Scholarship = beca (financial award for study)."
        },

        {
          type: "mc",
          q: "By the time she was accepted, Lucia had built up a ___ of 30 pieces.",
          options: ["building", "clay", "portfolio"],
          a: "portfolio",
          tip: "Build up = acumular/construir gradualmente.",
          successTip: "Build up = accumulate gradually. 'Build up a portfolio, build up experience, build up confidence.' Very useful phrasal verb! 📁"
        },

        {
          type: "mc",
          q: "Lucia: 'I had ___ my passion for clay long before I turned it into a career.'",
          options: ["discovered", "discover", "discovering"],
          a: "discovered",
          tip: "Had discovered = Past Perfect (earlier event)."
        },

        {
          type: "mc",
          q: "People said she had ___ the plot when she left her law career.",
          options: ["lost", "lose", "losing"],
          a: "lost",
          tip: "Lose → lost (irregular).",
          successTip: "'Lost the plot' = volverse loco/perder el rumbo. British idiom! 'She's lost the plot!' = ¡Se le fue la cabeza! Very common! 🤪"
        },

        {
          type: "order",
          q: "Traduce: 'Para cuando se graduó, ya había expuesto en cinco galerías.'",
          words: ["By", "the", "time", "she", "graduated,", "she", "had", "already", "exhibited", "in", "five", "galleries."],
          a: "By the time she graduated, she had already exhibited in five galleries.",
          extra: ["has", "done", "showed"],
          successTip:"“Exhibited” means showing artwork in a gallery or exhibition, while exposed usually means uncovered or revealed, so exhibited is the natural verb here."
        },

        {
          type: "mc",
          q: "Lucia had ___ her first sculpture from a single piece of clay before using a mould.",
          options: ["carved", "carve", "carving"],
          a: "carved",
          tip: "Carve = tallar/esculpir (cut into material).",
          successTip: "Carve = tallar. 'Carve a path' = abrirse camino (idiom). 'She carved her own path in the art world.' Inspiring! 🗿"
        },

        {
          type: "mc",
          q: "Before her first show, Lucia ___ never ___ so nervous in her life.",
          options: ["had / felt", "has / felt", "had / feel"],
          a: "had / felt",
          tip: "Had never felt = nunca había sentido."
        },

        {
          type: "input",
          q: "Curator's note: 'By the time the show opened, every piece had already been ___ at a good price.'",
          a: "sold",
          alts: ["Sold", "purchased", "bought", "reserved", "priced", "valued"],
          successTip: "'Sold out' = agotado. Phrasal verb! 'The show had already sold out before opening night.' Every artist's dream! 🎉"
        }
      ]
    },

    // ===================================================================
    // SET 6: BEFORE, AFTER & BY THE TIME (B2-)
    // ===================================================================
    {
      id: "m10-u5-set6",
      title: "Set 6: Connecting the Timeline",
      context: `*London gallery opening — Everyone meets*

Yuki, Omar, and Lucia are all at the same gallery opening.
They compare their journeys.
"It's funny," says Omar. "By the time we all got here, we had each taken a completely different road."

*Inauguración de galería en Londres — Todos se encuentran*

Yuki, Omar y Lucia están en la misma inauguración de galería.
Comparan sus trayectorias.
"Es curioso," dice Omar. "Para cuando llegamos aquí, cada uno había tomado un camino completamente diferente."`,

      questions: [
        {
          type: "match",
          q: "Match the time connectors:",
          pairs: [
            { left: "By the time", right: "Para cuando..." },
            { left: "Before", right: "Antes de..." },
            { left: "After", right: "Después de..." }
          ],
          a: "completed",
          successTip: "'By the time' = para cuando. Always followed by Past Simple + Past Perfect: 'By the time she arrived, he had left.' ⏱️"
        },

        {
          type: "mc",
          q: "___ the time Omar moved to London, he had taught for fifteen years.",
          options: ["By", "After", "Before"],
          a: "By",
          tip: "By the time = para cuando."
        },

        {
          type: "mc",
          q: "Yuki felt confident ___ she had practised for months.",
          options: ["after", "by the time", "before"],
          a: "after",
          tip: "After + having done = después de haber hecho.",
          successTip: "After she had practised = after practising. Both correct! In spoken English, people often drop 'had' after 'after'. 🗣️"
        },

        {
          type: "input",
          q: "Lucia's reflection: '___ ___ ___ the show opened, I had already met twenty new artists.'",
          a: "By the time",
          alts: ["by the time","before"],
          tip: "By the time = para cuando + Past Simple."
        },

        {
          type: "mc",
          q: "___ Omar had studied in Cairo, he moved to Paris to continue his training.",
          options: ["After", "By the time", "During"],
          a: "After",
          tip: "After + Past Perfect = sequence of events."
        },

        {
          type: "mc",
          q: "Yuki finished her first London piece ___ the semester had even officially started.",
          options: ["before", "after", "by the time"],
          a: "before",
          tip: "Before = antes de (the semester starting)."
        },

        {
          type: "order",
          q: "Traduce: 'Antes de que llegara Lucia, Omar ya había conocido a Yuki.'",
          words: ["Before", "Lucia", "arrived,", "Omar", "had", "already", "met", "Yuki."],
          a: "Before Lucia arrived, Omar had already met Yuki.",
          extra: ["After", "meet", "has"]
        },

        {
          type: "mc",
          q: "By the time the gallery closed, Yuki ___ sold four of her ceramics.",
          options: ["had", "has", "was"],
          a: "had",
          tip: "By the time + past = Past Perfect for the result.",
          successTip: "'By the time' + Past Simple triggers Past Perfect! It's almost automatic. Master this combo! 🔑"
        },

        {
          type: "mc",
          q: "Omar: 'I had never felt so at home ___ I walked into that gallery.'",
          options: ["until", "after", "during"],
          a: "until",
          tip: "'Had never... until' = nunca... hasta que.",
          successTip: "'Had never... until' = powerful structure! 'I had never cried at a painting until that day.' Emotional storytelling! 🎨"
        },

        {
          type: "input",
          q: "After Lucia ___ her first solo show, she was completely exhausted but happy.",
          a: "had",
          alts: ["Had"],
          successTip: "'After she had her show' = colloquial. 'Have a show' = idiom for performing/exhibiting. 'She's having a show next month!' 🎭"
        }
      ]
    },

    // ===================================================================
    // SET 7: LIFE TURNING POINTS (B2)
    // ===================================================================
    {
      id: "m10-u5-set7",
      title: "Set 7: The Moment Everything Changed",
      context: `*Mexico City — Carlos's story*

Carlos is 40 and one of Mexico's most respected ceramicists.
But his road was long and hard.
"Before I found ceramics, I had tried and failed at everything else."
"I was a late bloomer — but that's okay."

*Ciudad de México — La historia de Carlos*

Carlos tiene 40 años y es uno de los ceramistas más respetados de México.
Pero su camino fue largo y difícil.
"Antes de encontrar la cerámica, lo había intentado todo y había fracasado en todo."
"Tardé en florecer, pero está bien."`,

      questions: [
        {
          type: "mc",
          q: "Before Carlos found ceramics, he ___ in three different businesses.",
          options: ["had failed", "failed", "has failed"],
          a: "had failed",
          tip: "Earlier event = Past Perfect."
        },

        {
          type: "mc",
          q: "Carlos: 'I had ___ up hope before a friend introduced me to a pottery class.'",
          options: ["given", "gave", "give"],
          a: "given",
          tip: "Give up → given up (Past Perfect).",
          successTip: "Give up = rendirse. 'Don't give up!' = ¡No te rindas! Also: 'throw in the towel' = same meaning, boxing origin! 🥊"
        },

        {
          type: "input",
          q: "Carlos's story: 'By the time I ___ 35, I had restarted my career three times.'",
          a: "was",
        },

        {
          type: "mc",
          q: "He was a late ___. He only found his passion at 36.",
          options: ["bloomer", "starter", "beginner"],
          a: "bloomer",
          tip: "Late bloomer = persona que tarda en desarrollar su talento.",
          successTip: "'Late bloomer' = alguien que florece tarde. Beautiful idiom! 'She was a late bloomer but became the most successful in her class.' 🌸"
        },

        {
          type: "mc",
          q: "Before that pottery class, Carlos ___ never ___ anything with his hands.",
          options: ["had / made", "has / made", "had / make"],
          a: "had / made",
          tip: "Had never made = nunca había hecho."
        },

        {
          type: "match",
          q: "Match the turning point vocabulary:",
          pairs: [
            { left: "give up", right: "rendirse" },
            { left: "start over", right: "empezar de nuevo" },
            { left: "turn a corner", right: "pasar un momento difícil" }
          ],
          a: "completed",
          successTip: "'Turn a corner' = superar lo difícil y mejorar. 'After years of struggle, he finally turned a corner.' Very natural! 🔄"
        },

        {
          type: "mc",
          q: "Carlos: 'Once I ___ my first piece, I knew I had found my calling.'",
          options: ["had finished", "finished", "was finishing"],
          a: "had finished",
          tip: "Once + Past Perfect → Past Simple for the result.",
          successTip: "'Find your calling' = encontrar tu vocación. 'He had searched for years before he found his calling.' Powerful life phrase! ✨"
        },

        {
          type: "order",
          q: "Traduce: 'Después de que había fracasado tantas veces, finalmente encontró su camino.'",
          words: ["After", "he", "had", "failed", "so", "many", "times,", "he", "finally", "found", "his", "path."],
          a: "After he had failed so many times, he finally found his path.",
          successTip: "'found his way' is usually more natural and idiomatic for life direction"

        },

        {
          type: "mc",
          q: "By the time Carlos ___ 40, he had won two national art prizes.",
          options: ["turned", "had turned", "was turning"],
          a: "turned",
          tip: "Turned 40 = Past Simple. Won prizes = earlier = Past Perfect."
        },

        {
          type: "input",
          q: "Carlos's advice: 'It doesn't matter how many times you've ___ — what matters is that you get back up.'",
          a: "fallen",
          alts: ["Fallen","failed", "struggled", "lost", "tried", "been knocked down"],
          successTip: "'Get back up' = levantarse de nuevo. Idiom: 'Fall down seven times, get up eight.' Japanese proverb — very famous! 🥋"
        }
      ]
    },

    // ===================================================================
    // SET 8: THE ART WORLD — MASTERPIECES & MYSTERY (B2)
    // ===================================================================
    {
      id: "m10-u5-set8",
      title: "Set 8: The Art World's Dark Side",
      context: `*London — Omar investigates a forgery*

Omar is called to examine a suspicious painting.
The museum believed they had bought an original Rembrandt.
But something didn't feel right.
"By the time I arrived, the experts had already begun to doubt it."

*Londres — Omar investiga una falsificación*

Omar es llamado a examinar un cuadro sospechoso.
El museo creía que había comprado un Rembrandt original.
Pero algo no se sentía bien.
"Para cuando llegué, los expertos ya habían empezado a dudarlo."`,

      questions: [
        {
          type: "match",
          q: "Match the art world vocabulary:",
          pairs: [
            { left: "forgery", right: "falsificación" },
            { left: "masterpiece", right: "obra maestra" },
            { left: "curator", right: "curador/a" }
          ],
          a: "completed",
          successTip: "Forgery = falsificación. Verb: forge = falsificar. Also: forge a friendship/path = construir (positive meaning)! Same word! 🖼️"
        },

        {
          type: "mc",
          q: "The museum ___ the painting for £2 million before the doubts emerged.",
          options: ["had bought", "bought", "has bought"],
          a: "had bought",
          tip: "Purchase happened before the doubts = Past Perfect."
        },

        {
          type: "mc",
          q: "By the time Omar arrived, experts ___ already ___ that it was a forgery.",
          options: ["had / concluded", "have / concluded", "had / conclude"],
          a: "had / concluded",
          tip: "Had already concluded = Past Perfect.",
          successTip: "“Fake” is a general, everyday word for something false, while forgery is a more formal noun for an illegal falsification, especially in legal, financial, or art contexts."
        },

        {
          type: "input",
          q: "Investigation report: 'The forger had ___ the painting using materials from the wrong era.'",
          a: "created",
          alts: ["Created","forged", "copied", "recreated", "painted", "produced","made"],
          tip: "Create → created (regular past participle).",
          successTip: "Wrong era = era equivocada. Forgers get caught because they use modern materials. Art science is incredible! 🔬"
        },

        {
          type: "mc",
          q: "The ___ was restored before going on display — it had suffered damage.",
          options: ["masterpiece", "forgery", "canvas"],
          a: "masterpiece",
          tip: "Masterpiece = obra maestra (genuine great work)."
        },

        {
          type: "mc",
          q: "Omar: 'I had ___ several forgeries before, but this one was particularly convincing.'",
          options: ["seen", "saw", "see"],
          a: "seen",
          tip: "See → seen (Past Perfect).",
          successTip: "'Convincing' = convincente. Idiom: 'pull the wool over someone's eyes' = engañar a alguien. 'The forger pulled the wool over everyone's eyes!' 🐑"
        },

        {
          type: "match",
          q: "Match more art world vocabulary:",
          pairs: [
            { left: "auction", right: "subasta" },
            { left: "restoration", right: "restauración" },
            { left: "portfolio", right: "portafolio (de obras)" }
          ],
          a: "completed"
        },

        {
          type: "order",
          q: "Traduce: 'Para cuando se descubrió el fraude, el cuadro ya había sido vendido.'",
          words: ["By", "the", "time", "the", "fraud", "was", "discovered,", "the", "painting", "had", "already", "been", "sold."],
          a: "By the time the fraud was discovered, the painting had already been sold.",
          extra: ["sell", "fake", "they"]
        },

        {
          type: "mc",
          q: "The painting had been ___ — its true age was hidden under new paint.",
          options: ["altered", "restored", "framed"],
a: "altered",
          tip: "altered = alterado, restored = restaurado, framed = enmarcado.",
          successTip: "'You can also say: 'Tamper with' = manipular ilícitamente. Essential legal/crime English! ⚖️"
        },

        {
          type: "mc",
          q: "Omar concluded: 'Someone ___ this painting with extraordinary skill — but it wasn't Rembrandt.'",
          options: ["had made", "made", "has made"],
          a: "had made",
          tip: "Earlier event = Past Perfect.",
          successTip: "The art forgery world is fascinating! The famous forger Han van Meegeren fooled the Nazis. Had sold millions in fakes! 🎭"
        }
      ]
    },

    // ===================================================================
    // SET 9: REGRETS & REALISATIONS (B2+)
    // ===================================================================
    {
      id: "m10-u5-set9",
      title: "Set 9: If Only...",
      context: `*London studio — Lucia reflects*

After her first successful year, Lucia sits alone in her studio.
She thinks about the years she had wasted before finding sculpture.
"If only I had started earlier."
"I wish I had listened to my art teacher sooner."

*Estudio en Londres — Lucia reflexiona*

Después de su primer año exitoso, Lucia se sienta sola en su estudio.
Piensa en los años que había desperdiciado antes de encontrar la escultura.
"Ojalá hubiera empezado antes."
"Ojalá hubiera escuchado a mi profesor de arte antes."`,

      questions: [
        {
          type: "match",
          q: "Match the regret structures:",
          pairs: [
            { left: "I wish I had...", right: "Ojalá hubiera..." },
            { left: "If only I had...", right: "Si tan solo hubiera..." },
            { left: "I should have...", right: "Debería haber..." }
          ],
          a: "completed",
          successTip: "'I wish I had' = regret about the past. 'I wish I were' = regret about the present. Different forms, different timeframes! ⏰"
        },

        {
          type: "mc",
          q: "Lucia: 'I wish I ___ started sculpting when I was a teenager.'",
          options: ["had", "have", "was"],
          a: "had",
          tip: "Wish + had + past participle = past regret."
        },

        {
          type: "mc",
          q: "'If only I ___ listened to my instincts sooner!'",
          options: ["had", "have", "would"],
          a: "had",
          tip: "If only + had = past regret.",
          successTip: "'If only' is more emotional than 'I wish'! It expresses stronger regret. Great for dramatic writing! 😔"
        },

        {
          type: "input",
          q: "Lucia's diary: 'I should ___ ___ law school after the first year, not the third.'",
          a: "have left",
          alts: ["Have left", "have quit", "have dropped out"],
          tip: "Should have + past participle = debería haber."
        },

        {
          type: "mc",
          q: "Carlos told Lucia: 'Don't beat yourself up — we all get there in the end.'",
          options: [
            "Don't be too hard on yourself",
            "Don't fight",
            "Don't hurt yourself"
          ],
          a: "Don't be too hard on yourself",
          tip: "'Beat yourself up' = ser muy duro/a con uno mismo.",
          successTip: "'Beat yourself up' = torturarse mentalmente. 'Don't beat yourself up about it!' = muy común en inglés informal. 🥊💙"
        },

        {
          type: "mc",
          q: "Yuki: 'I wish I ___ more time in Tokyo before I left.'",
          options: ["had spent", "spent", "have spent"],
          a: "had spent",
          tip: "Wish + had spent = regret about past action."
        },

        {
          type: "order",
          q: "Traduce: 'Ojalá hubiera sabido entonces lo que sé ahora.'",
          words: ["I", "wish", "I", "had", "known", "then", "what", "I", "know", "now."],
          a: "I wish I had known then what I know now.",
          extra: ["have", "meet", "you"]
        },

        {
          type: "mc",
          q: "Omar regrets: 'It was my responsibility as a teacher. I ___ have pushed my students harder in those early years.'",
          options: ["should", "would", "could"],
          a: "should",
          tip: "Should have = missed obligation. Could have = missed opportunity. Would have = hypothetical (needs 'if').",
          successTip: "Should have vs Could have! SHOULD = moral obligation missed. COULD = possibility missed. Very different feelings! 🤔"
        },

        {
          type: "mc",
          q: "Carlos laughs: 'Hindsight is always 20/20. Don't dwell on it — move forward!'",
          options: [
            "It's easy to judge the past now that you know the outcome",
            "Your eyesight is perfect",
            "You should look back more often"
          ],
          a: "It's easy to judge the past now that you know the outcome",
          tip: "'Hindsight is 20/20' = es fácil ser sabio después del hecho.",
          successTip: "'Hindsight is 20/20' = Ya despues, todo se ve claro'. One of the most used idioms in English! Perfect for regret discussions! 👁️"
        },

        {
          type: "input",
          q: "Lucia's conclusion: 'I wish I ___ worried so much. Everything worked out in the end.'",
          a: "hadn't",
          alts: ["had not", "Hadn't"],
          successTip: "'Work out' = resultar bien. 'Don't worry, it'll work out!' = Phrasal verb used constantly for reassurance! Things work out! 🌟"
        }
      ]
    },

    // ===================================================================
    // SET 10: FULL NARRATIVE — PAST PERFECT IN STORIES (B2+)
    // ===================================================================
    {
      id: "m10-u5-set10",
      title: "Set 10: The Whole Story",
      context: `*London gallery — One year later*

Yuki, Omar, Lucia and Carlos stand together at a new exhibition.
It features all four of them — and it sold out within hours.
None of them had imagined, a year earlier, that they would be standing there together.

*Galería de Londres — Un año después*

Yuki, Omar, Lucia y Carlos están juntos en una nueva exposición.
Los presenta a los cuatro y se agotó en pocas horas.
Ninguno de ellos había imaginado, un año antes, que estarían ahí juntos.`,

      questions: [
        {
          type: "mc",
          q: "None of them ___ imagined that their paths would cross like this.",
          options: ["had", "have", "was"],
          a: "had",
          tip: "Thinking back from the past = Past Perfect."
        },

        {
          type: "mc",
          q: "By the time the exhibition ___, every piece ___ already been reserved.",
          options: ["opened / had", "had opened / has", "opened / was"],
          a: "opened / had",
          tip: "Opened = Past Simple. Had been = Past Perfect Passive.",
          successTip: "'Sold out' = agotado. Phrasal: 'It sold out in minutes!' = se agotó en minutos. Also used for betrayal: 'He sold out his friends.' 🎫"
        },

        {
          type: "order",
          q: "Traduce: 'Cuando se conocieron, cada uno ya había superado sus propias dificultades.'",
          words: ["When", "they", "met,", "each", "of", "them", "had", "already", "overcome", "their", "own", "struggles."],
          a: "When they met, each of them had already overcome their own struggles.",
        },

        {
          type: "mc",
          q: "Yuki: 'A year ago, I ___ never have believed this was possible.'",
          options: ["would", "had", "could have"],
          a: "would",
          tip: "Would never have = nunca hubiera creído.",
          successTip: "'Would never have believed' = nunca lo hubiera creído. Common structure for expressing amazement at your own journey! 🌠"
        },

        {
          type: "mc",
          q: "Carlos had ___ through so much before arriving at that moment of success.",
          options: ["been", "gone", "come"],
          a: "been",
          tip: "Had been through = haber pasado por algo.",
          successTip: "'Been through' = haber pasado por algo difícil. 'She had been through a lot.' Essential empathy phrase in English! 💪"
        },

        {
          type: "mc",
          q: "Omar told the journalist: 'Before tonight, I ___ never felt so proud of a group of artists.'",
          options: ["had", "have", "was"],
          a: "had",
          tip: "Had never felt = nunca había sentido (until that moment)."
        },

        {
          type: "order",
          q: "Traduce: 'Si tan solo hubieran sabido desde el principio cuánto tenían en común.'",
          words: ["If", "only", "they", "had", "known", "from", "the", "start", "how", "much", "they", "had", "in", "common."],
          a: "If only they had known from the start how much they had in common.",
          successTip: "'If only they had known' = Ojalá hubieran sabido. Perfect for expressing past regrets or missed connections! 💔"
        },

        {
          type: "mc",
          q: "Lucia: 'Everything I ___ gone through, had led me to exactly where I needed to be.'",
          options: ["had", "have", "was"],
          a: "had",
          tip: "Had gone through = Past Perfect (all the past experiences).",
          successTip: "'Exactly where I needed to be' = exactamente donde debía estar. Perfect closure phrase. Use it! Very natural and meaningful! 🎯"
        },

        {
          type: "mc",
          q: "The journalist wrote: 'Four artists who ___ almost given up — now selling out galleries worldwide.'",
          options: ["had", "have", "were"],
          a: "had",
          tip: "Past Perfect for the backstory before the present success."
        },
{
  type: "mc",
  q: "Final caption under their group photo: 'None of them ___ they would end up here.'",
  options: [
    "had ever imagined",
    "had ever been imagining",
    "have ever imagined"
  ],
  a: "had ever imagined",
  tip: "None of them = plural subject. Past Perfect for something that had never happened before that moment.",
  successTip: "🏆 UNIT 5 COMPLETE! Past Perfect mastered! You can tell full life stories with before, after, already, never, if only. That's real fluency! ✨🎨"
}

      ]
    }
  ],

  // ===================================================================
  // READING SET (B2)
  // ===================================================================
  readingSet: {
    id: "m10-u5-reading",
    title: "Reading: Four Artists, Four Stories",
    category: "reading",

    questions: [

      // --- PASSAGE 1 ---
      {
        type: "passage",
        passage: `**Yuki's Story — The Long Way Round**

By the time Yuki boarded her flight to London, she had already quit her job, sold her furniture, and failed her university entrance exam twice. She had spent three years working in an office before she finally decided to pursue art full-time.`
      },
      {
        type: "mc",
        q: "How many times had Yuki failed her entrance exam?",
        options: ["Twice", "Once", "Three times"],
        a: "Twice",
        tip: "'...failed her university entrance exam twice.'"
      },
      {
        type: "mc",
        q: "What had Yuki done before deciding to pursue art?",
        options: ["Worked in an office for three years", "Studied law", "Lived in Paris"],
        a: "Worked in an office for three years",
        tip: "'She had spent three years working in an office.'"
      },

      // --- PASSAGE 2 ---
      {
        type: "passage",
        passage: `**Omar's Story — Hands That Remember**

Omar had worked with clay since he was seven years old. By the time he became a teacher, he had already exhibited in galleries across Cairo, Beirut, and Istanbul. He had never considered leaving Egypt until a London gallery offered him a residency.`
      },
      {
        type: "mc",
        q: "How long had Omar been working with clay?",
        options: ["Since he was seven", "Since university", "For ten years"],
        a: "Since he was seven",
        tip: "'...had worked with clay since he was seven years old.'"
      },
      {
        type: "mc",
        q: "What made Omar consider leaving Egypt?",
        options: ["A London gallery offered him a residency", "He lost his teaching job", "He had a fight with his students"],
        a: "A London gallery offered him a residency",
        tip: "'...until a London gallery offered him a residency.'"
      },

      // --- PASSAGE 3 ---
      {
        type: "passage",
        passage: `**Lucia's Story — The Dropout Who Made It**

Lucia had dropped out of law school twice before she enrolled in art school at 24. People had told her she was making a mistake. But by the time she held her first solo exhibition, she had already proved them all wrong.`
      },
      {
        type: "mc",
        q: "How old was Lucia when she enrolled in art school?",
        options: ["24", "22", "28"],
        a: "24",
        tip: "'...enrolled in art school at 24.'"
      },
      {
        type: "mc",
        q: "What had people told Lucia?",
        options: ["She was making a mistake", "She was talented", "She should study ceramics"],
        a: "She was making a mistake",
        tip: "'People had told her she was making a mistake.'"
      },

      // --- PASSAGE 4 ---
      {
        type: "passage",
        passage: `**Carlos's Story — Better Late Than Never**

Carlos had tried and failed at three different careers before he discovered ceramics at 36. He had never touched clay before that first class. Within two years, he had won a national prize and opened his own studio in Mexico City.`
      },
      {
        type: "mc",
        q: "How many careers had Carlos tried before ceramics?",
        options: ["Three", "Two", "Four"],
        a: "Three",
        tip: "'...failed at three different careers.'"
      },
      {
        type: "mc",
        q: "What happened within two years of discovering ceramics?",
        options: ["He won a prize and opened a studio", "He moved to London", "He enrolled in art school"],
        a: "He won a prize and opened a studio",
        tip: "'...had won a national prize and opened his own studio.'"
      },

      // --- PASSAGE 5 ---
      {
        type: "passage",
        passage: `**The Exhibition — Where All Roads Led**

By the time the four artists met at the London gallery, each had overcome their own struggles. The show sold out within hours. None of them had imagined, just one year earlier, that they would be standing there together — successful, proud, and finally, exactly where they needed to be.`
      },
      {
        type: "mc",
        q: "How quickly did the show sell out?",
        options: ["Within hours", "Within days", "Within a week"],
        a: "Within hours",
        tip: "'The show sold out within hours.'"
      },
      {
        type: "mc",
        q: "What had none of them imagined a year earlier?",
        options: [
          "That they would be standing there together, successful",
          "That the show would be so expensive",
          "That London would be so cold"
        ],
        a: "That they would be standing there together, successful",
        tip: "'None of them had imagined...that they would be standing there together.'",
        successTip: "Beautiful story! Past Perfect told their backstories — all the had done, had tried, had failed — before their shared success. That's the power of this tense! 🌟"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (B2+)
  // ===================================================================
  reviewSet: {
    id: "m10-u5-review",
    title: "Unit 5 Review: Before & After",
    category: "review",

    questions: [
      {
        type: "mc",
        q: "Review: Before Yuki arrived in London, she ___ already ___ her job.",
        options: ["had / quit", "has / quit", "had / quitted"],
        a: "had / quit",
        tip: "Earlier event = had + past participle."
      },
      {
        type: "mc",
        q: "Review: Omar had ___ seen so many masterpieces in one place.",
        options: ["never", "already", "just"],
        a: "never",
        tip: "Had never = nunca había."
      },
      {
        type: "mc",
        q: "Review: ___ the time the show opened, every piece had been reserved.",
        options: ["By", "After", "Before"],
        a: "By",
        tip: "By the time = para cuando."
      },
      {
        type: "mc",
        q: "Review: Lucia wished she ___ started sculpting earlier.",
        options: ["had", "have", "was"],
        a: "had",
        tip: "Wish + had + participle = past regret."
      },
      {
        type: "mc",
        q: "Review: Carlos ___ never touched clay before that first class.",
        options: ["had", "has", "was"],
        a: "had",
        tip: "Had never = Past Perfect negative."
      },
      {
        type: "mc",
        q: "Review: 'She ___ the plot' — British idiom for going crazy.",
        options: ["lost", "dropped", "missed"],
        a: "lost",
        tip: "Lost the plot = se le fue la cabeza."
      },
      {
        type: "mc",
        q: "Review: After she ___ her portfolio, she applied for the scholarship.",
        options: ["had built up", "build up", "was building"],
        a: "had built up",
        tip: "After + Past Perfect = earlier completed action."
      },
      {
        type: "mc",
        q: "Review: 'I should ___ listened to my instincts sooner.'",
        options: ["have", "had", "of"],
        a: "have",
        tip: "Should have + past participle = past regret.",
        successTip: "Should OF is a common mistake! It's always should HAVE (not 'of'). They sound similar when spoken quickly! ⚠️"
      },
      {
        type: "mc",
        q: "Review: The museum had ___ the painting before the forgery was discovered.",
        options: ["bought", "buy", "been buying"],
        a: "bought",
        tip: "Buy → bought (irregular Past Perfect)."
      },
      {
        type: "mc",
        q: "Review: None of them ___ imagined they would all end up at the same gallery.",
        options: ["had", "have", "were"],
        a: "had",
        tip: "Past Perfect for unimaginable earlier thought.",
        successTip: "🎉 UNIT 5 MASTERED! Past Perfect ✅ Art vocabulary ✅ Regrets ✅ Idioms & phrasal verbs ✅ Ready for Unit 6! 🚀🎨"
      }
    ]
  }
};
